import './App.css';
import React, { Suspense, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import CustomErrorBoundary from '../src/components/error-handling/CustomBoundaryComponent';
import { Provider, useSelector, useDispatch } from 'react-redux';
import routes from './routes';
import store from './store';
import { getAuthDetails } from './utils/helper';
import toneicon from './assests/images/tone-icon.png';
import Login from './components/Auth/Login';
import Sidebar from './components/includes/Sidebar';
import Navbar from './components/Layout/Navbar';
import DashBoardPage from './components/Home/Dashboard';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import Loader from './components/includes/AlertComponent';
import Logout from './components/Auth/Logout';
import { PermissionProvider } from './contexts/PermissionContext';
import TokenExpiry from './components/Auth/TokenExpiry';
import Otp from './components/Auth/Otp';
import { ThemeProvider } from './contexts/ThemeContext';
import './assests/css/theme.css';
import { useAlert, createAlertHelpers } from './components/Layout/useAlert';
import { ToastProvider } from './components/Layout/Toast';
import UserLogActivity from './components/userActivity/UserActivity';
import { CONSTANTS } from './utils/constants';
import NoInternet from './components/includes/NoInternet';
import { useTheme } from '../src/contexts/ThemeContext';
import { RootState, AppDispatch } from '../src/store';
import { getUserByIdTrigger } from '../src/slices/userSlice';

const EmitterSoftware = React.lazy(() => import('./components/EmitterSoftware/EmitterSoftware'));

const AuthenticatedRoutes: React.FC = () => {
  const { addUserActivity } = UserLogActivity();
  const isAuthenticated = getAuthDetails();
  const location = useLocation();
  const navigate = useNavigate();
  const { colors, updateColors, isDarkMode, toggleDarkMode } = useTheme();
  const { showAlert, AlertComponent } = useAlert();
  const alertHelpers = createAlertHelpers(showAlert);

  const menuName = localStorage.getItem('menuName');
  const dispatch = useDispatch<AppDispatch>();
  const selectedUser = useSelector((state: RootState) => state.user.selectedUser);

  const handleNavigate = () => {
    addUserActivity(
      {},
      `Navigate from ${menuName} to Home`,
      'User navigated to the home page',
      `${menuName}`,
    );
    navigate('/home');
  };
  useEffect(() => {
    if (!isAuthenticated) return;
    updateColors({
      ...colors,
      primary: isAuthenticated.user.primaryColor
        ? isAuthenticated.user.primaryColor
        : CONSTANTS.DEFAULT_PRIMARY_COLOR,
      secondary: isAuthenticated.user.secondaryColor
        ? isAuthenticated.user.secondaryColor
        : CONSTANTS.DEFAULT_SECONDARY_COLOR,
    });
    dispatch(getUserByIdTrigger(isAuthenticated.user.id));
  }, [dispatch]);

  if (!isAuthenticated) {
    return <Navigate to="/" />;
  } else if (isAuthenticated && (location.pathname == '/' || location.pathname == '/login')) {
    return <Navigate to="/home" />;
  }

  const profilePath = selectedUser.data?.profile;
  const cfBaseUrl = process.env.REACT_APP_CF_STORAGE_BASE_URL;
  const profileImageUrl = profilePath
    ? `${cfBaseUrl}/${process.env.REACT_APP_PROFILE_FOLDER}/${profilePath.replace(/\\/g, '/')}`
    : '';

  function signOut() {
    alertHelpers.logoutConfirm().then((result) => {
      if (result.isConfirmed) {
        document.documentElement.style.setProperty(
          '--primary-color',
          CONSTANTS.DEFAULT_PRIMARY_COLOR,
        );
        document.documentElement.style.setProperty(
          '--secondary-color',
          CONSTANTS.DEFAULT_SECONDARY_COLOR,
        );
        localStorage.removeItem('otpVerified');
        navigate('/logout');
        alertHelpers.success('Logged Out', 'You have been successfully logged out.');
        addUserActivity(
          {},
          'User Logged Out',
          'User has successfully logged out from the application.',
          'Profile',
        );
      }
    });
  }
  return (
    <CustomErrorBoundary>
      <Suspense fallback={<Loader isLoading={true} />}>
        {/* <AutoLogout /> */}
        <AlertComponent />
        <Routes>
          {routes.map((route, index) => (
            <Route
              key={index}
              path={route.path}
              element={
                <PermissionProvider>
                  <route.layout>
                    <div style={{ height: '100vh', display: 'flex' }}>
                      <Sidebar />
                      <div className="super-dashboard">
                        <Navbar
                          menuName={menuName || 'Dashboard'}
                          isDarkMode={isDarkMode}
                          onToggleDarkMode={toggleDarkMode}
                          onNavigateHome={handleNavigate}
                          userProfile={{
                            name: `${isAuthenticated.user.firstname} ${isAuthenticated.user.lastname}`,
                            email: isAuthenticated.user.email,
                            avatar: profileImageUrl || toneicon,
                          }}
                          onSignOut={signOut}
                          onProfileClick={() => {
                            localStorage.setItem('menuName', 'About');
                            // Navigate to profile if needed
                          }}
                        />
                        <div>{React.createElement(route.component)}</div>
                      </div>
                    </div>
                  </route.layout>
                </PermissionProvider>
              }
            />
          ))}
          <Route
            path="/home"
            element={isAuthenticated ? <DashBoardPage /> : <Navigate to="/login" />}
          />
          <Route path="/emitter-software" element={<EmitterSoftware />} />
          <Route path="*" element={<Navigate to="/home" />} />
        </Routes>
      </Suspense>
    </CustomErrorBoundary>
  );
};

const AppContent = () => {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/token-expiry" element={<TokenExpiry />} />
        <Route path="/" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/otp/:token" element={<Otp />} />
        <Route path="/internet-connection" element={<NoInternet />} />
        <Route path="*" element={<AuthenticatedRoutes />} />
      </Routes>
    </Router>
  );
};

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider>
        <ToastProvider>
          <AppContent />
        </ToastProvider>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
